/**
 * CollectionPhonePricing
 * Batch-fetches Plus Pricing Voice & Data for phone product cards
 * and updates the DOM to show "From $X/mo for 24 months | $0 Upfront | 0% APR".
 * Falls back to the Shopify price when the API has no data for a phone.
 */
(function () {
  'use strict';

  const API_BASE = 'https://shopify-manager-grc7a7fpbee0eqdx.canadaeast-01.azurewebsites.net';
  const ENDPOINT = '/products/api/card-pricing/';
  const BATCH_SIZE = 50;

  // In-memory cache so we don't re-fetch known IDs across re-renders
  const priceCache = new Map();

  // Track in-flight product IDs (so overlapping triggers don't double-fetch the
  // same card) plus bounded per-id retry counts, and a debounce handle.
  var inFlight = {};
  var retryCount = {};
  var pendingProcess = null;

  // ------------------------------------------------------------------
  // Inject styles (self-contained, like plan-builder-cart-enhancer.js)
  // ------------------------------------------------------------------
  function injectStyles() {
    if (document.getElementById('collection-phone-pricing-styles')) return;
    const style = document.createElement('style');
    style.id = 'collection-phone-pricing-styles';
    style.textContent = `
      .card-enhanced-pricing {
        width: 100%;
        text-align: right;
      }
      .card-pricing-main {
        font-size: 14px;
        line-height: 1.3;
        color: var(--color-foreground);
      }
      .card-pricing-from,
      .card-pricing-term {
        font-size: 12px;
        opacity: 0.7;
      }
      .card-pricing-amount {
        font-size: 18px;
      }
      .card-pricing-details {
        font-size: 11px;
        opacity: 0.6;
        margin-top: 6px;
      }
      .card-pricing-retail {
        margin-top: 9px;
        font-size: 13px;
        opacity: 0.9;
        font-weight: 500;
      }
      .card-pricing-separator {
        margin: 0 3px;
      }
      .card-pricing-savings-badge {
        display: inline-block;
        background: linear-gradient(90deg, #253746 0%, #E44398 100%);
        color: #fff;
        font-size: 13px;
        font-weight: 700;
        padding: 5px 12px;
        border-radius: 4px;
        letter-spacing: 0.3px;
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 2;
      }
      .card-pricing-zero {
        font-size: 18px;
      }
      /* Mobile adjustments */
      @media (max-width: 768px) {
        .card-enhanced-pricing {
          text-align: left;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // ------------------------------------------------------------------
  // Debounced processCards to prevent rapid-fire calls
  // ------------------------------------------------------------------
  function scheduleProcess(delay) {
    if (pendingProcess) clearTimeout(pendingProcess);
    pendingProcess = setTimeout(function () {
      pendingProcess = null;
      processCards();
    }, delay || 100);
  }

  // ------------------------------------------------------------------
  // Core: find cards, fetch pricing, update DOM
  // ------------------------------------------------------------------
  function processCards() {
    var cards = document.querySelectorAll('[data-phone-product="true"]:not([data-pricing-enhanced])');
    if (!cards.length) return;

    // Collect IDs that need fetching
    var idsToFetch = [];
    var cardsByProductId = {};

    cards.forEach(function (card) {
      var productId = card.getAttribute('data-product-id');
      if (!productId) return;

      // Group cards by product ID (same product can appear in multiple sections)
      if (!cardsByProductId[productId]) {
        cardsByProductId[productId] = [];
      }
      cardsByProductId[productId].push(card);

      // Only fetch if not cached and not already being fetched.
      if (!priceCache.has(productId) && !inFlight[productId]) {
        idsToFetch.push(productId);
      }
    });

    // Apply cached pricing immediately
    Object.keys(cardsByProductId).forEach(function (id) {
      if (priceCache.has(id)) {
        cardsByProductId[id].forEach(function (card) {
          applyPricing(card, priceCache.get(id));
        });
      }
    });

    // Fetch any uncached IDs in batches. Independent batches are fine — the
    // inFlight guard prevents double-fetching the same ID.
    if (idsToFetch.length === 0) return;

    var uniqueIds = Array.from(new Set(idsToFetch));
    for (var i = 0; i < uniqueIds.length; i += BATCH_SIZE) {
      var batch = uniqueIds.slice(i, i + BATCH_SIZE);
      fetchBatch(batch, cardsByProductId);
    }
  }

  function fetchBatch(ids, cardsByProductId) {
    var url = API_BASE + ENDPOINT + '?ids=' + ids.join(',');
    ids.forEach(function (id) { inFlight[id] = true; });

    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('Card pricing API error: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var pricing = data.pricing || {};

        // Cache and apply each result
        ids.forEach(function (id) {
          delete inFlight[id];
          var pricingData = pricing[id] || null;
          priceCache.set(id, pricingData);

          if (cardsByProductId[id]) {
            cardsByProductId[id].forEach(function (card) {
              applyPricing(card, pricingData);
            });
          }
        });

        // Pick up any cards that appeared (e.g. the dropdown opened) or were
        // skipped while this batch was in flight.
        scheduleProcess(50);
      })
      .catch(function (err) {
        console.warn('CollectionPhonePricing: fetch failed, will retry.', err);
        var willRetry = false;
        ids.forEach(function (id) {
          delete inFlight[id];
          retryCount[id] = (retryCount[id] || 0) + 1;
          if (retryCount[id] >= 3) {
            // Give up after a few attempts — leave the Shopify price (fallback).
            priceCache.set(id, null);
            if (cardsByProductId[id]) {
              cardsByProductId[id].forEach(function (card) {
                card.setAttribute('data-pricing-enhanced', 'fallback');
              });
            }
          } else {
            // Leave the card unenhanced so a later pass retries it.
            willRetry = true;
          }
        });
        if (willRetry) scheduleProcess(1500);
      });
  }

  function applyPricing(card, pricingData) {
    // Mark as processed
    card.setAttribute('data-pricing-enhanced', 'true');

    if (!pricingData || !pricingData.available) {
      // No API data — keep Shopify price visible (fallback)
      return;
    }

    var target = card.querySelector('[data-card-pricing-target]');
    if (!target) return;

    // Populate the amount
    var amountEl = target.querySelector('.card-pricing-amount');
    if (amountEl) {
      amountEl.textContent = '$' + formatPrice(pricingData.monthly_payment);
    }

    // Populate the retail (outright) price as a third row, when provided
    var retailRow = target.querySelector('.card-pricing-retail');
    var retailAmt = target.querySelector('.card-pricing-retail-amount');
    if (retailRow && retailAmt && pricingData.retail_price > 0) {
      retailAmt.textContent = '$' + formatPrice(pricingData.retail_price);
      retailRow.classList.remove('hidden');
    } else if (retailRow) {
      retailRow.classList.add('hidden');
    }

    // Show the content
    var contentEl = target.querySelector('.card-pricing-content');
    if (contentEl) {
      contentEl.classList.remove('hidden');
    }

    // Handle sale pricing — show "Save $X" badge on card image (top-right)
    if (pricingData.is_on_sale && pricingData.savings_amount > 0) {
      if (!card.querySelector('.card-pricing-savings-badge')) {
        var mediaEl = card.querySelector('.product-card__media');
        if (mediaEl) {
          var badge = document.createElement('span');
          badge.className = 'card-pricing-savings-badge';
          badge.textContent = 'Save $' + formatPrice(pricingData.savings_amount);
          mediaEl.appendChild(badge);
        }
      }
    }

    // Show enhanced pricing, hide Shopify price and default sale badge
    target.classList.remove('hidden');
    var shopifyPrice = card.querySelector('.price');
    if (shopifyPrice) {
      shopifyPrice.style.display = 'none';
    }
    var shopifySaleBadge = card.querySelector('.badge--onsale');
    if (shopifySaleBadge) {
      shopifySaleBadge.style.display = 'none';
    }
  }

  function formatPrice(value) {
    var num = parseFloat(value);
    if (isNaN(num)) return '0.00';
    // Show whole number if .00, otherwise 2 decimals
    return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
  }

  // ------------------------------------------------------------------
  // Initialization + event listeners
  // ------------------------------------------------------------------
  function init() {
    injectStyles();
    processCards();

    // Mega-menu / header cards can render a moment after load; run a couple of
    // extra passes so their pricing is fetched in the background at page load
    // rather than waiting for the user to hover the dropdown.
    setTimeout(processCards, 600);
    setTimeout(processCards, 1800);

    // Re-process after collection filter/sort (dispatched by collection.js)
    document.addEventListener('collection:reloaded', function () {
      scheduleProcess(150);
    });

    // Observe the whole document for newly inserted phone cards (infinite
    // scroll, mega-menu hydration, section swaps, etc.) so their pricing is
    // fetched as soon as they appear — not only when hovered.
    var observer = new MutationObserver(function (mutations) {
      var hasNewCards = mutations.some(function (m) {
        return Array.prototype.some.call(m.addedNodes, function (n) {
          if (n.nodeType !== 1) return false;
          return (n.matches && n.matches('[data-phone-product="true"]')) ||
                 (n.querySelector && n.querySelector('[data-phone-product="true"]'));
        });
      });
      if (hasNewCards) scheduleProcess(120);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Mega nav — process on mouseenter for the shop dropdown
    document.addEventListener('mouseenter', function (e) {
      // Guard: text nodes don't have .closest()
      if (!e.target || !e.target.closest) return;
      var megaNav = e.target.closest('.mega-menu, .mega-menu__container, .mega-menu__panel, .header__submenu, [data-mega-menu]');
      if (megaNav) {
        scheduleProcess(150);
      }
    }, true);

    // Featured collection tabs — click delegation
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var tab = e.target.closest('[data-tab], .featured-collection__tab, .slider__nav-button');
      if (tab) {
        scheduleProcess(350);
      }
    });

    // Shopify section rendering events
    document.addEventListener('shopify:section:load', function () {
      scheduleProcess(250);
    });
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
